import React, { useRef, useMemo, forwardRef, useImperativeHandle } from 'react';
import { useFrame, extend, useThree } from '@react-three/fiber';
import { shaderMaterial, useTexture } from '@react-three/drei';
import * as THREE from 'three';
import TrajectoryArc from './TrajectoryArc';

// Define a set of color palettes for the planets
const palettes = [
  [
    new THREE.Color('#0b0178'), new THREE.Color('#0b0178'),
    new THREE.Color('#090257'), new THREE.Color('#2a002e'),
    new THREE.Color('#6A0E00'), new THREE.Color('#bd6c02'),
  ],
  [
    new THREE.Color('#000138'), new THREE.Color('#000147'),
    new THREE.Color('#360059'), new THREE.Color('#60008a'),
    new THREE.Color('#7902ad'), new THREE.Color('#00d9ff'),
  ],
  [
    new THREE.Color('#290000'), new THREE.Color('#2e0106'),
    new THREE.Color('#570101'), new THREE.Color('#660000'),
    new THREE.Color('#a62502'), new THREE.Color('#ffae00'),
  ],
];

// This is the shader for the planet surface.
const PlanetMaterial = shaderMaterial(
  {
    uTime: 0.0,
    uFocus: 0.0,
    lightDirection: new THREE.Vector3(0.5, -0.8, 0.5).normalize(),
    glowPower: 1.2,
    uGradientColors: palettes[0],
    uOpacity: 1.0, 
    uMousePos: new THREE.Vector2(0, 0),
    uGlowRadius: 0.08,
    uGlowColor: new THREE.Color('#F7921E'), 
    uGlowIntensity: 0.0,
    uAspect: 1.0,
    uTexture: null,
  },
  ` 
    varying vec3 vNormal;
    varying vec4 vClipPosition;
    varying vec2 vUv;
    void main() {
      vNormal = normalize(normalMatrix * normal);
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      vClipPosition = gl_Position;
      vUv = uv;
    }
  `,
  ` 
    uniform float uTime;
    uniform float uFocus;
    uniform vec3 lightDirection;
    uniform vec3 uGradientColors[6];
    uniform float glowPower;
    uniform float uOpacity;
    uniform vec2 uMousePos;
    uniform float uGlowRadius;
    uniform vec3 uGlowColor;
    uniform float uGlowIntensity;
    uniform float uAspect;
    uniform sampler2D uTexture; 

    varying vec3 vNormal;
    varying vec4 vClipPosition;
    varying vec2 vUv;
    
    vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
    vec2 mod289(vec2 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
    vec3 permute(vec3 x) { return mod289(((x*34.0)+1.0)*x); }
    float snoise(vec2 v) {
        const vec4 C = vec4(0.211324865405187, 0.366025403784439, -0.577350269189626, 0.024390243902439);
        vec2 i  = floor(v + dot(v, C.yy) );
        vec2 x0 = v -   i + dot(i, C.xx);
        vec2 i1;
        i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
        vec4 x12 = x0.xyxy + C.xxzz;
        x12.xy -= i1;
        i = mod289(i);
        vec3 p = permute( permute( i.y + vec3(0.0, i1.y, 1.0 )) + i.x + vec3(0.0, i1.x, 1.0 ));
        vec3 m = max(0.5 - vec3(dot(x0,x0), dot(x12.xy,x12.xy), dot(x12.zw,x12.zw)), 0.0);
        m = m*m;
        m = m*m;
        vec3 x = 2.0 * fract(p * C.www) - 1.0;
        vec3 h = abs(x) - 0.5;
        vec3 ox = floor(x + 0.5);
        vec3 a0 = x - ox;
        m *= 1.79284291400159 - 0.85373472095314 * ( a0*a0 + h*h );
        vec3 g;
        g.x  = a0.x  * x0.x  + h.x  * x0.y;
        g.yz = a0.yz * x12.xz + h.yz * x12.yw;
        return 130.0 * dot(m, g);
    }

    float remap(float value, float from1, float to1, float from2, float to2) {
      return from2 + (value - from1) * (to2 - from2) / (to1 - from1);
    }
    
    void main() {
        float distortion = snoise(vUv * 8.0 + uTime * 0.1) * 0.01;
        vec2 distortedUv = vUv + distortion;

        vec4 texColor = texture2D(uTexture, distortedUv);
        float intensity = dot(vNormal, lightDirection);
        float colorIndex = remap(intensity, -1.0, 1.0, 0.0, 5.0);
        int index1 = int(floor(colorIndex));
        int index2 = min(index1 + 1, 5);
        float mixFactor = fract(colorIndex);
        vec3 color1 = uGradientColors[index1];
        vec3 color2 = uGradientColors[index2];
        vec3 gradientColor = mix(color1, color2, mixFactor);
        vec3 texturedPlanetColor = gradientColor * texColor.rgb;
        float visibilityFactor = pow(uOpacity, 2.0);
        vec3 basePlanetColor = texturedPlanetColor * glowPower * visibilityFactor;
        
        vec2 ndc = vClipPosition.xy / vClipPosition.w;
        vec2 correctedMouse = uMousePos * vec2(uAspect, 1.0);
        vec2 correctedNdc = ndc * vec2(uAspect, 1.0);
        float dist = distance(correctedNdc, correctedMouse);
        float spotlight = 1.0 - smoothstep(0.0, uGlowRadius, dist);
        
        vec3 finalOutputColor = basePlanetColor + (uGlowColor * spotlight * uGlowIntensity);
        gl_FragColor = vec4(finalOutputColor, uOpacity);
    }
  `
);

extend({ PlanetMaterial });

function getRandomPointOnSphere(radius) {
  const u = Math.random();
  const v = Math.random();
  const theta = 2 * Math.PI * u;
  const phi = Math.acos(2 * v - 1);
  const x = radius * Math.sin(phi) * Math.cos(theta);
  const y = radius * Math.sin(phi) * Math.sin(theta);
  const z = radius * Math.cos(phi);
  return [x, y, z];
}

const Earth = forwardRef(({ colorIndex = 0, index, onPlanetClick, onPlanetHover, ...props }, ref) => {
  const groupRef = useRef();
  const texture = useTexture('/clouds-new.png');
  const baseRotationSpeed = useMemo(() => Math.random() * 0.005 + 0.002, []);
  const currentRotationSpeed = useRef(baseRotationSpeed);
  
  const { mouse, size } = useThree();
  const isHovered = useRef(false);
  const focus = useRef(0.0);

  const baseScale = useRef(new THREE.Vector3(1, 1, 1));
  const hoverScaleMultiplier = useRef(1.0);
  const currentGlowPower = useRef(1.2); 

  const arcData = useMemo(() => {
    const data = [];
    const numArcs = 15;
    for (let i = 0; i < numArcs; i++) {
      data.push({
        start: getRandomPointOnSphere(1),
        end: getRandomPointOnSphere(1),
        speed: Math.random() * 0.4 + 0.1,
        color: i < 3 ? '#F7921E' : '#F7921E'
      });
    }
    return data;
  }, []);

  const material = useMemo(() => {
    const mat = new PlanetMaterial();
    mat.uniforms.uGradientColors.value = palettes[colorIndex % palettes.length];
    mat.uniforms.uTexture.value = texture;
    return mat;
  }, [colorIndex, texture]);

  const arcsRef = useRef();
  
  useFrame(({ clock }) => {
    const targetRotationSpeed = isHovered.current ? baseRotationSpeed * 2.5 : baseRotationSpeed;
    const targetScaleMultiplier = isHovered.current ? 1.1 : 1.0;
    const targetGlowPower = isHovered.current ? 1.5 : 1.2;

    currentRotationSpeed.current = THREE.MathUtils.lerp(currentRotationSpeed.current, targetRotationSpeed, 0.1);
    hoverScaleMultiplier.current = THREE.MathUtils.lerp(hoverScaleMultiplier.current, targetScaleMultiplier, 0.1);
    currentGlowPower.current = THREE.MathUtils.lerp(currentGlowPower.current, targetGlowPower, 0.1);

    if (groupRef.current) {
      groupRef.current.rotation.y += currentRotationSpeed.current;
      groupRef.current.scale.copy(baseScale.current).multiplyScalar(hoverScaleMultiplier.current);
    }

    if (material) {
        material.uniforms.uTime.value = clock.elapsedTime;
        material.uniforms.uFocus.value = focus.current;
        material.uniforms.uMousePos.value = mouse;
        material.uniforms.uAspect.value = size.width / size.height;
        material.uniforms.glowPower.value = currentGlowPower.current;

        const breathingSpeed = 1.0;
        const minIntensity = 0.35;
        const maxIntensity = 0.65;
        const sineWave = (Math.sin(clock.elapsedTime * breathingSpeed) + 1) / 2;
        const breathingIntensity = minIntensity + sineWave * (maxIntensity - minIntensity);

        const targetIntensity = isHovered.current ? breathingIntensity : 0.0;
        material.uniforms.uGlowIntensity.value = THREE.MathUtils.lerp(
            material.uniforms.uGlowIntensity.value,
            targetIntensity,
            0.1 
        );
    }

    if (arcsRef.current) {
        arcsRef.current.children.forEach(child => {
            if (child.material?.uniforms?.uFocus) {
                child.material.uniforms.uFocus.value = focus.current;
            }
        });
    }
  });

  useImperativeHandle(ref, () => ({
    group: groupRef.current,
    update: (position, scale, opacity, focusAmount) => {
      if (groupRef.current) {
        groupRef.current.position.copy(position);
        baseScale.current.copy(scale);
      }
      if (material) {
        material.uniforms.uOpacity.value = opacity;
        focus.current = THREE.MathUtils.lerp(focus.current, focusAmount, 0.05);
      }
    }
  }));

  const handlePointerOver = () => {
    isHovered.current = true;
    if (onPlanetHover) onPlanetHover(index, true); // Notify App.js that this planet is hovered
    const cursor = document.querySelector('.comet-head');
    if (cursor) cursor.classList.add('cursor-interactive-hover');
  };

  const handlePointerOut = () => {
    isHovered.current = false;
    if (onPlanetHover) onPlanetHover(index, false); // Notify App.js that this planet is no longer hovered
    const cursor = document.querySelector('.comet-head');
    if (cursor) cursor.classList.remove('cursor-interactive-hover');
  };

  return (
    <group ref={groupRef} {...props} onPointerOver={handlePointerOver} onPointerOut={handlePointerOut} onClick={() => onPlanetClick(index)}>
      <mesh>
        <sphereGeometry args={[1, 64, 64]} />
        <primitive object={material} attach="material" />
      </mesh>
      <mesh rotation-x={Math.PI / 2.3}>
        <torusGeometry args={[1.5, 0.015, 24, 128]} />
        <primitive object={material} attach="material" />
      </mesh>
      
      <group ref={arcsRef}>
        {arcData.map((arc, i) => (
          <TrajectoryArc
            key={i}
            start={arc.start}
            end={arc.end}
            speed={arc.speed}
            color={arc.color}
          />
        ))}
      </group>
    </group>
  );
});

export default Earth;